import { useCallback } from "react";
import { useFieldErrorActions } from "@/shared/contexts/field-error-context";
import { normalizeError } from "@/shared/errors/normalizeError";
import {
  markEnvelopeHandled,
  routeErrorEnvelopeToast,
} from "@/shared/errors/routeErrorEnvelope";
import {
  API_ERROR_CODES,
  type ApiErrorEnvelope,
} from "@/shared/types/apiError";

export interface UseApiErrorOptions {
  /**
   * If provided AND the envelope carries field errors, route them to the
   * FieldErrorContext keyed by this formId so inline <FieldError /> components
   * render. Without formId, validation errors fall through to a toast.
   */
  formId?: string;
  /**
   * Skip surface rendering and just return the normalized envelope. Rare —
   * useful when the caller wants to drive their own UI from the envelope.
   */
  silent?: boolean;
}

/**
 * Central error router. Consume any caught error from a mutation/query and
 * dispatch it to the right rendering surface (inline / toast / modal / banner)
 * per the rules in SPEC_DEMO2FEEDBACK_ERROR_MESSAGE_UX.
 *
 * Inline-handled errors (validation routed to FieldErrorContext) are also
 * marked in the toast dedup map so the global MutationCache fallback in
 * queryClient.ts doesn't double-render them as a toast.
 *
 * Form usage:
 *   const handleApiError = useApiError();
 *   useMutation({
 *     mutationFn,
 *     onError: (err) => handleApiError(err, { formId: "createOrder" }),
 *   });
 */
export const useApiError = () => {
  const { setFieldErrors } = useFieldErrorActions();

  return useCallback(
    (err: unknown, opts: UseApiErrorOptions = {}): ApiErrorEnvelope => {
      const env = normalizeError(err);
      if (opts.silent) {
        // Caller drives their own UI; still mark handled so the global
        // fallback doesn't surprise them with a toast.
        markEnvelopeHandled(env);
        return env;
      }

      // Validation / field-bound error → inline if the form opted in.
      const isValidation =
        env.code === API_ERROR_CODES.ValidationFailed ||
        env.fieldErrors.length > 0;
      if (isValidation && opts.formId) {
        setFieldErrors(opts.formId, env.fieldErrors);
        markEnvelopeHandled(env);
        return env;
      }

      routeErrorEnvelopeToast(env);
      return env;
    },
    [setFieldErrors]
  );
};
